import flow from 'lodash/fp/flow';
import forEach from 'lodash/fp/forEach';
import get from 'lodash/fp/get';

import {filterTreeWithDescendants} from '@utils/treeFiltering';

export const LOCATION_TYPES = {
  COUNTRY: 'country',
};

/**
 * Parses the data object of a geo-locations.getLocations API request.
 *
 * The result objects is updated with value and title properties. As this
 * tree is being updated, the node references are also returned in a
 * flattened list and a dictionary to reference by id.
 *
 * @param getLocationsApiResult
 * @returns {{tree: *, list: *, dict}}
 */
export function getLocationsTreeFromResult(getLocationsApiResult, processFn) {
  return flow([
    get(['data', 'locations']),
    forEach((location) => {
      processFn(location);
    }),
  ])(getLocationsApiResult);
}

export function parseApiResult(getLocationsApiResult) {
  const dict = {};
  const list = [];

  function setLocationsFields(location) {
    const currentLocationId = location.id.internal.toString();
    dict[currentLocationId] = location;
    list.push(location);

    location.value = location.id.internal;
    location.title = location.name;

    // Temp sorting until can be discussed with services/product regarding languages and their sorting
    location.children
      ?.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      .forEach((location) => {
        location.parentId = currentLocationId;
        setLocationsFields(location);
      });
  }

  const tree = getLocationsTreeFromResult(getLocationsApiResult, setLocationsFields);

  return {
    tree: tree,
    dict: dict,
    list: list,
  };
}

/**
 * Given the list of locations, return an alphabetically sorted list of countries only.
 *
 * @param {Array} locationsList
 * @param {boolean} convertLocationIdsToIntegers - The locationIds are strings by default (e.g. "92"). If true, we convert these IDs to integers
 * @returns {Array}
 */
export function getSortedListOfCountries(locationsList, convertLocationIdsToIntegers) {
  let sortedListOfCountries = locationsList
    .filter((location) => location.tier === '3')
    .sort((a, b) => a?.title.localeCompare(b?.title));

  if (convertLocationIdsToIntegers) {
    sortedListOfCountries = sortedListOfCountries.map((country) => ({...country, value: parseInt(country.value, 10)}));
  }

  return sortedListOfCountries;
}

/**
 * Returns the string name (title) corresponding to the
 * location found in the dictionary with the provided id.
 *
 * @param locationDict
 * @param locationId
 * @returns {*}
 */
export function getLocationName(locationDict, locationId) {
  return locationDict?.[locationId]?.title;
}

/**
 * Returns the string names (titles) corresponding to the
 * locations found in the dictionary for the provided locationIds.
 *
 * @param locationDict
 * @param locationIds
 * @returns {*}
 */
export function getLocationNames(locationDict, locationIds) {
  return locationIds.map((locationId) => getLocationName(locationDict, locationId));
}

/**
 * Filters location tree for filter components.
 * When a parent location matches, ALL child locations are included for full context.
 *
 * The search does one full pass of the tree using a Depth-First recursion approach. The search also clones the tree.
 * It therefore requires O(n) time and O(n) space, where n is the number of nodes in the tree.
 *
 * @param tree the location tree
 * @param keywords {string} keyword to include in the search
 * @returns {object} The filtered location tree
 */
export function filterTreeWithChildren(tree, keywords) {
  return filterTreeWithDescendants(tree, keywords, (node, searchValue) => {
    return node.title?.toLowerCase().includes(searchValue.toLowerCase()) ?? false;
  });
}
